import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function TableSessionOrdersModal({
  open,
  onOpenChange,
  session,
  onRequestBill,
  isRequesting,
  themeColor = "#f97316"
}) {
  const { t } = useTranslation();
  const formatPrice = (amount) => `${amount.toFixed(2).replace(".", ",")}€`;

  if (!session) return null;

  const { orders = [], total = 0, customer_name, table_number } = session;

  const statusLabel = (status) => {
    if (status === "pending") return t("tableSession.inKitchen", "In kitchen");
    if (status === "preparing") return t("tableSession.preparing", "Preparing");
    if (status === "ready") return t("tableSession.ready", "Ready");
    if (status === "delivered") return t("tableSession.delivered", "Delivered");
    return status;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white dark:bg-zinc-900 max-h-[80vh] overflow-hidden flex flex-col" data-testid="session-orders-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t("tableSession.yourOrders", "Your Orders — Table {{number}}", { number: table_number })}</span>
            <span className="text-sm font-normal text-gray-500">{customer_name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2">
          {orders.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              {t("tableSession.noOrdersYet", "No orders yet")}
            </p>
          ) : (
            orders.map((order, idx) => (
              <div
                key={order.id}
                className="p-3 rounded-lg border border-gray-100 dark:border-gray-800"
                data-testid={`session-order-${idx}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-400">
                    {t("tableSession.order", "Order #{{number}}", { number: order.order_number || order.id?.slice(0, 6) })}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                    {statusLabel(order.status)}
                  </span>
                </div>
                <div className="space-y-1">
                  {(order.items || []).map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.quantity}x {item.product_name || item.name}
                      </span>
                      <span className="font-medium">
                        {formatPrice(item.quantity * item.unit_price)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 flex justify-between">
                  <span className="text-sm text-gray-500">{t("tableSession.subtotal", "Subtotal")}</span>
                  <span className="font-bold">{formatPrice(order.total)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {orders.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex justify-between items-center text-lg font-bold">
              <span>{t("tableSession.tableTotal", "Table Total")}</span>
              <span style={{ color: themeColor }}>{formatPrice(total)}</span>
            </div>

            <Button
              onClick={onRequestBill}
              disabled={isRequesting}
              className="w-full h-12 text-white font-bold text-base"
              style={{ backgroundColor: themeColor }}
              data-testid="modal-request-bill-btn"
            >
              {isRequesting ? t("tableSession.requestingBill", "Requesting bill...") : t("tableSession.requestBillBtn", "Request the Bill")}
            </Button>

            <p className="text-xs text-center text-gray-400">
              {t("tableSession.staffComing", "A staff member will come to your table to complete payment")}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
